import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { Users, Factory, ClipboardCheck, Stamp, IndianRupee, FileText, CreditCard, ArrowRight, Loader2, AlertTriangle, Clock } from "lucide-react";

function UsageCard({ label, used, limit, extra, icon: Icon, testid }) {
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : null;
  return (
    <div data-testid={testid} className="rounded-lg border bg-white p-4">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        <Icon className="h-4 w-4 text-slate-400" />
      </div>
      <p className="mt-2 font-heading text-2xl font-semibold" data-testid={`${testid}-value`}>
        {used}
        <span className="text-sm font-normal text-muted-foreground"> / {limit == null ? "Unlimited" : limit}</span>
      </p>
      {extra ? <p className="mt-0.5 text-xs text-muted-foreground">{extra}</p> : null}
      {limit != null && (
        <div className="mt-2 h-1.5 rounded-full bg-slate-100">
          <div className={`h-1.5 rounded-full transition-all duration-300 ${pct >= 90 ? "bg-red-500" : pct >= 70 ? "bg-amber-500" : "bg-slate-900"}`}
            style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="rounded-lg border bg-white p-4">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        <Icon className="h-4 w-4 text-slate-400" />
      </div>
      <p className="mt-2 font-heading text-2xl font-semibold" data-testid={`${testid}-value`}>{value}</p>
    </div>
  );
}

export default function EnterpriseDashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [supplierReqs, setSupplierReqs] = useState([]);

  useEffect(() => {
    api.get("/enterprise/overview").then((r) => setData(r.data)).catch((e) => setError(e?.response?.data?.detail || "Failed to load"));
    api.get("/supplier-requests").then((r) => setSupplierReqs(r.data.requests)).catch(() => {});
  }, []);

  if (error) return <p data-testid="enterprise-dashboard-error" className="text-sm text-red-600">{error}</p>;
  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const { enterprise, subscription, pending_request, usage, spend } = data;
  const plan = subscription?.plan;

  return (
    <div className="space-y-6" data-testid="enterprise-dashboard">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight" data-testid="enterprise-name">{enterprise.name}</h1>
        <p className="mt-1 text-sm text-muted-foreground">Enterprise control center — usage, spend and subscription at a glance.</p>
      </div>

      {pending_request && (
        <div data-testid="pending-subscription-banner" className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <Clock className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div>
            <p className="text-sm font-semibold text-amber-900">Subscription request pending activation</p>
            <p className="mt-0.5 text-sm text-amber-800">
              Your request for the <span className="font-medium">{pending_request.plan?.name || pending_request.plan_id}</span> plan is awaiting payment confirmation. Plan limits activate once payment is confirmed.
            </p>
          </div>
        </div>
      )}

      {!subscription && !pending_request && (
        <div data-testid="no-subscription-banner" className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
          <div>
            <p className="text-sm font-semibold text-red-900">No active subscription</p>
            <p className="mt-0.5 text-sm text-red-800">
              Choose a plan to unlock buyer invitations and workspace limits.{" "}
              <Link to="/enterprise/subscription" data-testid="banner-choose-plan-link" className="font-medium text-blue-700 hover:underline">View plans</Link>
            </p>
          </div>
        </div>
      )}

      <div data-testid="subscription-card" className="flex flex-wrap items-center gap-4 rounded-lg border bg-white p-5">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-900 text-white"><CreditCard className="h-5 w-5" /></div>
        <div className="min-w-40">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Subscription</p>
          <p className="font-heading text-lg font-semibold" data-testid="subscription-plan-name">{plan ? plan.name : "No plan"}</p>
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Status</p>
          <span data-testid="subscription-status" className={`mt-0.5 inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${
            subscription ? "bg-green-100 text-green-800" : pending_request ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`}>
            {subscription ? "Active" : pending_request ? "Pending activation" : "None"}
          </span>
        </div>
        {subscription?.period_end && (
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Valid until</p>
            <p className="text-sm font-medium" data-testid="subscription-period-end">{new Date(subscription.period_end).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</p>
          </div>
        )}
        {plan?.price_inr != null && (
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Price</p>
            <p className="text-sm font-medium">{fmtMoney(plan.price_inr)}/year</p>
          </div>
        )}
        <Link to="/enterprise/subscription" data-testid="manage-subscription-link"
          className="ml-auto flex items-center gap-1 rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-800">
          Manage subscription <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold">Usage & plan limits</h2>
          <Link to="/enterprise/buyers" data-testid="manage-team-link" className="flex items-center gap-1 text-sm font-medium text-blue-700 hover:underline">
            Manage team <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <UsageCard label="Buyers" used={usage.buyers.used} limit={usage.buyers.limit} icon={Users} testid="usage-buyers"
            extra={usage.pending_invitations ? `${usage.pending_invitations} invitation(s) pending` : ""} />
          <UsageCard label="Suppliers" used={usage.suppliers.used} limit={usage.suppliers.limit} icon={Factory} testid="usage-suppliers" />
          <UsageCard label="Technical Reviewers" used={usage.technical_reviewers.used} limit={usage.technical_reviewers.limit} icon={ClipboardCheck} testid="usage-reviewers" />
          <UsageCard label="Approvers" used={usage.approvers.used} limit={usage.approvers.limit} icon={Stamp} testid="usage-approvers" />
        </div>
      </section>

      <section data-testid="supplier-approvals-section">
        <Link to="/enterprise/supplier-requests" data-testid="pending-supplier-requests-card"
          className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-white p-4 transition-colors hover:bg-slate-50">
          <div className="flex items-center gap-3">
            <Factory className="h-5 w-5 text-slate-400" />
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Pending Supplier Requests</p>
              <p className="font-heading text-xl font-semibold" data-testid="pending-supplier-requests-count">
                {supplierReqs.filter((r) => r.status === "pending").length}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-muted-foreground">Approved suppliers: <span className="font-semibold text-slate-900">{usage.suppliers.used}</span></span>
            <span className="text-muted-foreground">Rejected: <span className="font-semibold text-slate-900">{supplierReqs.filter((r) => r.status === "rejected").length}</span></span>
            <ArrowRight className="h-4 w-4 text-slate-400" />
          </div>
        </Link>
      </section>

      <section>
        <h2 className="mb-3 font-heading text-lg font-semibold">Spend overview</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <Kpi label="Total PO Value" value={fmtMoney(spend.total_po_value)} icon={IndianRupee} testid="spend-total" />
          <Kpi label="Active POs" value={spend.active_pos} icon={FileText} testid="spend-active-pos" />
          <Kpi label="Delivered POs" value={spend.delivered_pos} icon={FileText} testid="spend-delivered-pos" />
          <Kpi label="Delayed POs" value={spend.delayed_pos} icon={AlertTriangle} testid="spend-delayed-pos" />
          <Kpi label="Open Enquiries" value={spend.open_enquiries} icon={FileText} testid="spend-open-enquiries" />
        </div>
      </section>
    </div>
  );
}
